import { deviceNames, type DeviceName } from '@athena-lib/device';
import { objectFromEntries } from '@athena-lib/object-utils';
import type { ColumnWidth, ResponsiveColumnWidth } from './types';

type ColumnWidthNumbers = Exclude<ColumnWidth, 'auto' | 'content'>;
const columnWidthNumbers: Record<ColumnWidthNumbers, number> = {
  '1/5': 1 / 5,
  '1/4': 1 / 4,
  '1/3': 1 / 3,
  '2/5': 2 / 5,
  '1/2': 1 / 2,
  '3/5': 3 / 5,
  '2/3': 2 / 3,
  '3/4': 3 / 4,
  '4/5': 4 / 5,
};

export const containerWidthFromColumns = (
  width: ColumnWidth | ResponsiveColumnWidth | undefined,
  collapseBelow: DeviceName | undefined,
  collapseForAllDevices: boolean | undefined,
  allWidths: Array<ColumnWidth | ResponsiveColumnWidth> | undefined,
): Record<DeviceName, number> => {
  const columnWidthsPerDevice: Partial<Record<DeviceName, number>> = fillCollapsedWidths(
    collapseBelow,
    collapseForAllDevices,
  );

  const fill = (
    widthNumber: number,
    maybePartialWidths: Partial<Record<DeviceName, number>> = {},
  ) => ({
    ...objectFromEntries(deviceNames.map((deviceName) => [deviceName, widthNumber])),
    ...maybePartialWidths,
  });

  if (!width) {
    return fill(1, columnWidthsPerDevice);
  }

  if (typeof width === 'string') {
    width = { sm: width, md: width, lg: width };
  }

  for (const deviceName of deviceNames) {
    if (deviceName in columnWidthsPerDevice) {
      continue;
    }

    const widthInDevice = width[deviceName]!;
    if (widthInDevice !== 'auto' && widthInDevice !== 'content') {
      const widthNumber = columnWidthNumbers[widthInDevice];
      columnWidthsPerDevice[deviceName] = widthNumber;
    } else if (allWidths) {
      columnWidthsPerDevice[deviceName] = estimateColumnWidth(deviceName, allWidths);
    }
  }

  // If we missed width information for any device, we default to 1
  return fill(1, columnWidthsPerDevice);
};

const fillCollapsedWidths = (
  collapseBelow: DeviceName | undefined,
  collapseForAllDevices: boolean | undefined,
) => {
  const columnWidthsPerDevice: Partial<Record<DeviceName, number>> = {};
  if (collapseBelow || collapseForAllDevices) {
    for (const deviceName of deviceNames) {
      if (deviceName === collapseBelow && !collapseForAllDevices) {
        break;
      }
      columnWidthsPerDevice[deviceName] = 1;
    }
  }
  return columnWidthsPerDevice;
};

export const estimateColumnWidth = (
  deviceName: DeviceName,
  allWidths: Array<ColumnWidth | ResponsiveColumnWidth>,
) => {
  let totalKnownWidths = 0;
  let unknownWidthsCount = 0;
  for (const columnWidth of allWidths) {
    const columnWidthInDevice =
      typeof columnWidth === 'string' ? columnWidth : columnWidth[deviceName]!;

    if (columnWidthInDevice in columnWidthNumbers) {
      totalKnownWidths += columnWidthNumbers[columnWidthInDevice as ColumnWidthNumbers];
    } else {
      unknownWidthsCount++;
    }
  }

  const knownWidth = totalKnownWidths;
  return (1 - knownWidth) / unknownWidthsCount;
};
